import Box from "@/components/Box";
import { ExternalTextLink } from "@/components/Links";
import Loading from "@/components/Loading";
import Badge from "@/tpo/Badge";
import ChevronComponent from "@/tpo/Chevron";
import Group from "@/tpo/Group";
import LinkWrapper from "@/tpo/LinkWrapper";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";

import {
  cmpHomeDashboardEmptyMessage,
  cmpHomeDashboardListInnerStackGap,
  cmpHomeDashboardListPrimary,
  cmpHomeDashboardListSecondary,
  cmpHomeDashboardSectionRowGap
} from "./dashboardLayout";
import formatPromoSchedule from "./formatPromoSchedule";

const PROMO_TYPE_LABELS = {
  news: "News",
  offer: "Offer",
  education: "Education",
  event: "Event",
  webinar: "Webinar",
  course: "Course"
};

const PARTNERS_PINK_PROMO_TYPES = new Set(["news", "offer"]);

function promoTypeBadgeProps(type) {
  if (PARTNERS_PINK_PROMO_TYPES.has(type)) {
    return { bg: "partners", color: "dark" };
  }
  return { bg: "purple", color: "white" };
}

const promoScheduleStyles = {
  fontFamily: "gilroyBold",
  fontSize: 14,
  lineHeight: "20px",
  whiteSpace: "nowrap",
  flexShrink: 0
};

function PromoBookNowButton({ promo }) {
  if (!promo.link) {
    return null;
  }

  const isExternal = promo.linkType === "external";

  return (
    <ButtonV2
      color="dark"
      size="xs"
      as={isExternal ? ExternalTextLink : LinkWrapper}
      href={isExternal ? promo.link : undefined}
      to={isExternal ? undefined : promo.link}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      underline={false}
      rightIcon={<ChevronComponent direction="bottom" />}
    >
      {promo.buttonText}
    </ButtonV2>
  );
}

function PromoCard({ promo, hasDivider }) {
  const scheduleLabel = formatPromoSchedule(promo);

  return (
    <Stack width="100%" borderBottom={hasDivider ? "1px solid" : undefined} borderColor="lightGrey">
      <Stack gap={cmpHomeDashboardSectionRowGap} px={0} pt={[16, 16, 16]} pb={[28, 28, 28]}>
        <Stack gap={8}>
          {promo.type || scheduleLabel ? (
            <Group
              width="100%"
              alignItems="center"
              justifyContent={promo.type ? "space-between" : "flex-end"}
              gap={8}
            >
              {promo.type ? (
                <Badge
                  size="xs"
                  textTransform="uppercase"
                  flexShrink={0}
                  {...promoTypeBadgeProps(promo.type)}
                >
                  {PROMO_TYPE_LABELS[promo.type] ?? promo.type}
                </Badge>
              ) : null}
              {scheduleLabel ? <Box {...promoScheduleStyles}>{scheduleLabel}</Box> : null}
            </Group>
          ) : null}
          <Group width="100%" alignItems="flex-start" gap={cmpHomeDashboardSectionRowGap}>
            {promo.imageUrl ? (
              <Box
                as="img"
                src={promo.imageUrl}
                alt=""
                width={80}
                height={80}
                minWidth={80}
                borderRadius={8}
                flexShrink={0}
                display="block"
                style={{ objectFit: "cover" }}
              />
            ) : null}
            <Stack gap={cmpHomeDashboardListInnerStackGap} flex={1} minWidth={0}>
              <Box {...cmpHomeDashboardListPrimary}>{promo.title}</Box>
              {promo.subtitle ? (
                <Box {...cmpHomeDashboardListSecondary}>{promo.subtitle}</Box>
              ) : null}
            </Stack>
          </Group>
        </Stack>
        {promo.link ? (
          <Group width="100%" justifyContent="flex-start">
            <PromoBookNowButton promo={promo} />
          </Group>
        ) : null}
      </Stack>
    </Stack>
  );
}

export default function NewsPromoList({ promos, loading, emptyMessage = "No news at the moment" }) {
  if (loading && !promos?.length) {
    return <Loading />;
  }

  if (!promos?.length) {
    return <Box {...cmpHomeDashboardEmptyMessage}>{emptyMessage}</Box>;
  }

  return (
    <Stack gap={0} width="100%" px={0}>
      {promos.map((promo, index) => (
        <PromoCard key={promo.id} promo={promo} hasDivider={index < promos.length - 1} />
      ))}
    </Stack>
  );
}
